/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  TMyHubActionType,
  TMyHubBulkActionType,
  TMyHubType
} from "../../model/types";
import type {
  TCarouselItem,
  TMyHubCarousel,
  TMyHubCarouselList,
  TMyHubItemUnion,
  TMyHubCarouselHeader,
  TRecordingMyHubItem
} from "../../model/types";
import { Locale, LocaleKeys } from "../../model/locale";
import { CarouselTemplate } from "../../model/enums";
import { mapMyHubItemToCarouselItem } from "./myHubMappers";
import { MyHubFilterId } from "./myHub.types";
import type { TMyHubSavedItem, TMyHubSavedListSummary } from "./myHub.types";

/**
 * Mappers for the flat card list shared by the Saved and Deleted tabs
 * (Figma: My Hub Detail — nodes 115:780 and 71:2130).
 *
 * Everything the rows display is composed here rather than in the view, so the
 * wording, date format and duration rounding are unit-testable without React and
 * so `MyHubSavedList` stays a pure renderer.
 *
 * The two tabs render the same row: only the dated line differs ("Saved on …"
 * vs "Deleted on …") and the Deleted thumbnails carry no duration caption. Both
 * differences are resolved from the active filter here rather than by a second
 * mapper, so the tabs cannot drift apart.
 */

/**
 * Tile template whose pixel dimensions the Saved thumbnails are requested at.
 *
 * The rows carry the plain 16 ∶ 9 landscape art (Figma node 115:780): they are
 * a flat list of cards, and nothing on them ever converts to a PIP video, so
 * the non-PIP template is the one that describes them. It measures 21.927vw ×
 * 9/16 — 421 × 237 px at the 1920 base, the nearest template above the 360 ×
 * 203 slot the card renders the art in.
 */
const SAVED_THUMBNAIL_TEMPLATE: CarouselTemplate =
  CarouselTemplate.LANDSCAPE_HORIZONTAL;

/** Separator between the show name and the episode line — Figma uses spaces. */
const HEADLINE_SEPARATOR: string = "  -  ";

/** Middot separator used between the episode label and the episode title. */
const MIDDOT: string = "‧";

/** Seconds per minute — the wire states a recording's duration in seconds. */
const SECONDS_PER_MINUTE: number = 60;

const MINUTES_PER_HOUR: number = 60;

/** Matches any `{{token}}` placeholder, named or positional. */
const PLACEHOLDER_PATTERN: RegExp = /\{\{[^{}]+\}\}/g;

/**
 * Substitutes up to two placeholders into a locale string, by position.
 *
 * `Locale.getString` returns the raw template, and the codebase has no shared
 * interpolation helper, so this local one fills the first placeholder it finds
 * with `first` and the second with `second`, whatever those tokens are named.
 *
 * Matching on order rather than on token name is deliberate: EN now uses named
 * `{{camelCase}}` tokens (docs/localise-manual-workflow.md), while the ES/FR
 * bundles still carry positional `{{0}}` / `{{1}}` until the next Lokalise pull
 * renames them from EN. Order is the one thing both formats agree on.
 *
 * Exported so `myHubSeriesMappers` can compose against the same helper instead
 * of keeping a second implementation of it.
 *
 * @param template - Locale string, possibly containing `{{token}}` placeholders.
 * @param first - Value for the first placeholder.
 * @param second - Value for the second, when the template takes two.
 * @returns The interpolated string.
 */
export const interpolate = (
  template: string,
  first: string,
  second?: string
): string => {
  const values: string[] = second === undefined ? [first] : [first, second];
  let index: number = 0;
  return template.replaceAll(PLACEHOLDER_PATTERN, (token: string): string => {
    const value: string | undefined = values[index];
    index += 1;
    return value === undefined ? token : value;
  });
};

/**
 * Formats an ISO date-time as the short numeric date the Saved rows use
 * ("5/18/2026").
 *
 * Uses the locale-aware `toLocaleDateString` with explicit numeric parts so the
 * order follows the viewer's locale rather than being hardcoded month-first.
 *
 * @param isoDateTime - ISO 8601 date-time from the API, possibly absent.
 * @returns The formatted date, or `""` when the input is missing or unparseable.
 */
export const formatSavedDate = (isoDateTime: string | undefined): string => {
  if (isoDateTime === undefined || isoDateTime === "") {
    return "";
  }

  const parsed: Date = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric"
  });
};

/**
 * Formats a duration in minutes as the coarse wording used by the thumbnail
 * caption: "1 hour", "2 hours", "45 min".
 *
 * Whole hours read as hours; anything else reads in minutes, because the caption
 * is an at-a-glance size hint rather than an exact runtime.
 *
 * @param durationMinutes - Duration in minutes, possibly absent.
 * @returns The formatted duration, or `""` when there is nothing to show.
 */
export const formatSavedDuration = (
  durationSeconds: number | undefined
): string => {
  if (
    durationSeconds === undefined ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds <= 0
  ) {
    return "";
  }

  const minutes: number = Math.round(durationSeconds / SECONDS_PER_MINUTE);
  const isWholeHours: boolean = minutes % MINUTES_PER_HOUR === 0;

  if (!isWholeHours) {
    return interpolate(
      Locale.getString(LocaleKeys.MY_HUB_SAVED_DURATION_MINUTES),
      String(minutes)
    );
  }

  const hours: number = minutes / MINUTES_PER_HOUR;
  const template: string =
    hours === 1
      ? Locale.getString(LocaleKeys.MY_HUB_SAVED_DURATION_HOUR)
      : Locale.getString(LocaleKeys.MY_HUB_SAVED_DURATION_HOURS);

  return interpolate(template, String(hours));
};

/**
 * Composes the episode line: "S5 E10 ‧ Life is a Promise", degrading to
 * whichever half the API supplied.
 *
 * @param item - The saved recording.
 * @returns The episode line, or `""` when the item has neither numbering nor an
 * episode title (a movie, typically).
 */
const composeEpisodeLine = (item: TRecordingMyHubItem): string => {
  const hasNumbering: boolean =
    item.seasonOrYear !== undefined && item.episodeNumber !== undefined;
  const numbering: string = hasNumbering
    ? interpolate(
        Locale.getString(LocaleKeys.CONTENT_ACTION_SUFFIX_SEASON_EPISODE),
        String(item.seasonOrYear),
        String(item.episodeNumber)
      )
    : "";

  const episodeTitle: string = item.episodeTitle || "";

  if (numbering !== "" && episodeTitle !== "") {
    return `${numbering} ${MIDDOT} ${episodeTitle}`;
  }
  return numbering !== "" ? numbering : episodeTitle;
};

/**
 * Composes the row headline — "Yellowstone  -  S5 E10 ‧ Life is a Promise".
 *
 * The design renders this as a single line rather than a title with a subtitle
 * beneath it, so it is composed here and passed to the card as one string.
 *
 * @param item - The saved recording.
 * @returns The headline, falling back to the bare title for movies and specials.
 */
export const composeSavedHeadline = (item: TRecordingMyHubItem): string => {
  const episodeLine: string = composeEpisodeLine(item);
  if (episodeLine === "") {
    return item.title;
  }
  return item.title + HEADLINE_SEPARATOR + episodeLine;
};

/**
 * Actions the flat list has no button for.
 *
 * Listed as the exclusion rather than the inclusion so a newly supported action
 * needs only its icon added to `savedActionCatalog` — the two cannot fall out of
 * step by forgetting this list. EXTEND is the only one: the design gives it no
 * glyph. Mirrors the popup's list in `recordingHighlightMappers`.
 */
const UNRENDERABLE_ACTIONS: readonly TMyHubActionType[] = [
  TMyHubActionType.EXTEND
];

/**
 * The subset of a row's actions the list can render, in the service's order.
 *
 * Applied here rather than at render time because the list's LEFT/RIGHT bounds
 * and its OK handler both index the stored `availableActions` (see
 * `savedActionCount` and `resolveFocusedRowAction` in myHubSlice): anything the
 * view would drop has to be gone before the row reaches the store, or the button
 * under the cursor and the button on screen drift apart — the D-pad would fire
 * column *n* of the raw list while the user is looking at rendered button *n*.
 *
 * Exported because every surface that builds a {@link TMyHubSavedItem} owes the
 * same guarantee: `MyHubSavedList` is the one list behind the Saved and Deleted
 * tabs, the manage-series screen and the scheduled-single screen, so a row
 * mapped by `myHubSeriesMappers` reaches exactly the same bounds and the same OK
 * handler as one mapped here.
 *
 * @param availableActions - Action types reported by the API for one row.
 * @returns The renderable subset, order preserved.
 */
export const toRenderableRowActions = (
  availableActions: readonly TMyHubActionType[] | undefined
): TMyHubActionType[] => {
  if (availableActions === undefined) {
    return [];
  }
  return availableActions.filter(
    (actionType: TMyHubActionType): boolean =>
      !UNRENDERABLE_ACTIONS.includes(actionType)
  );
};

/**
 * Maps one recording onto its presentation-ready row.
 *
 * @param item - A recording from `GET /v1/myHub/carousels?myHubFilter=<filter>`.
 * @param imageHostBaseUrl - Image host resolved once per fetch.
 * @param filter - The tab being mapped; decides the dated line and whether the
 * thumbnail carries its duration caption. Anything other than DELETED is mapped
 * as a Saved row.
 * @returns The row the flat list renders.
 */
export const mapMyHubSavedItem = (
  item: TRecordingMyHubItem,
  imageHostBaseUrl: string = "",
  filter: MyHubFilterId = MyHubFilterId.SAVED
): TMyHubSavedItem => {
  const isDeleted: boolean = filter === MyHubFilterId.DELETED;
  const datedOn: string = formatSavedDate(
    isDeleted ? item.deletedOn : item.savedOn
  );
  const expiresOnDate: string = formatSavedDate(item.expiresOn);
  // Figma 71:2130 gives the Deleted thumbnails no caption — the row's dated line
  // already says everything the tab has to say about it.
  const durationText: string = isDeleted
    ? ""
    : formatSavedDuration(item.duration);

  // Thumbnail resolution and watch progress are already solved for every MyHub
  // item type by the carousel mapper — reuse it rather than re-deriving the
  // image-type rules here. The tile is also carried on the row whole, so a
  // pressed action has the offer type and airing window it acts on.
  const tile: TCarouselItem = mapMyHubItemToCarouselItem(
    item,
    imageHostBaseUrl,
    SAVED_THUMBNAIL_TEMPLATE
  );

  const labelFor = (key: string, value: string): string =>
    value === "" ? "" : interpolate(Locale.getString(key), value);

  return {
    id: item.myHubItemId,
    recordingId: item.recordingId,
    contentId: item.contentId,
    headline: composeSavedHeadline(item),
    dateLabel: labelFor(
      isDeleted ? LocaleKeys.MY_HUB_DELETED_ON : LocaleKeys.MY_HUB_SAVED_ON,
      datedOn
    ),
    expiresOnLabel: labelFor(LocaleKeys.MY_HUB_SAVED_EXPIRES_ON, expiresOnDate),
    durationLabel: labelFor(
      LocaleKeys.MY_HUB_SAVED_THUMBNAIL_CAPTION,
      durationText
    ),
    imageUrl: tile.imageUrl,
    progressPercent: tile.progressPercent,
    // Narrowed, and copied rather than aliased — the row goes into Redux state
    // and must not share an array with the raw API payload.
    availableActions: toRenderableRowActions(item.availableActions),
    tile
  };
};

/**
 * Maps the carousel header onto the summary line above the list.
 *
 * @param header - Header from the API, absent on tabs that have none.
 * @returns The summary, or `null` when the API sent no header — the view then
 * omits the line rather than rendering zeroes it cannot vouch for.
 */
export const mapMyHubSavedSummary = (
  header: TMyHubCarouselHeader | undefined
): TMyHubSavedListSummary | null => {
  if (header === undefined) {
    return null;
  }

  return {
    totalCount: header.totalCount || 0,
    usedMinutes: header.usedMinutes || 0,
    limitMinutes: header.limitMinutes || 0,
    // `||` rather than `??` or a ternary: every TMyHubBulkActionType value is a
    // non-empty string, so `undefined` is the only falsy case it can catch.
    bulkAction: header.bulkAction || null
  };
};

/**
 * Flattens a flat-list tab's response into the list the view renders.
 *
 * The endpoint returns Saved and Deleted as carousels for shape consistency with
 * the other tabs, but both designs are a single flat list — so every recording
 * across every returned carousel is concatenated in order. Non-recording items
 * (folders, plain content) are dropped: a row needs its dated field and a
 * `recordingId` to render its copy and to service its actions.
 *
 * The summary line comes from the header on the carousel carrying one. These
 * tabs return exactly one carousel, but reading the first header present rather
 * than the first carousel's keeps the line rendering if the service ever splits
 * the list across rows — and it is where build 421 moved the header to, from
 * the response body.
 *
 * @param response - The screen-load response for `myHubFilter=saved|deleted`.
 * @param imageHostBaseUrl - Image host resolved once per fetch.
 * @param filter - The tab being mapped — see {@link mapMyHubSavedItem}.
 * @returns The rows, the summary line, and the paging identity (the one
 * carousel's id and its `resolvedCount`) `loadMoreMyHubSavedList` pages
 * further with.
 */
export const mapMyHubSavedList = (
  response: TMyHubCarouselList,
  imageHostBaseUrl: string = "",
  filter: MyHubFilterId = MyHubFilterId.SAVED
): {
  items: TMyHubSavedItem[];
  summary: TMyHubSavedListSummary | null;
  carouselId: string | null;
  resolvedCount: number | null;
} => {
  const items: TMyHubSavedItem[] = response.carousels
    .flatMap((carousel): TMyHubItemUnion[] => carousel.items)
    .filter(
      (item: TMyHubItemUnion): item is TRecordingMyHubItem =>
        item.myHubItemType === TMyHubType.RECORDING
    )
    .map(
      (item: TRecordingMyHubItem): TMyHubSavedItem =>
        mapMyHubSavedItem(item, imageHostBaseUrl, filter)
    );

  const headerCarousel: TMyHubCarousel | undefined = response.carousels.find(
    (carousel: TMyHubCarousel): boolean => carousel.header !== undefined
  );

  // These tabs return exactly one carousel (see this function's own doc
  // comment above) — its id and resolvedCount are what further pages key off.
  const carousel: TMyHubCarousel | undefined = response.carousels[0];

  return {
    items,
    summary: mapMyHubSavedSummary(
      headerCarousel === undefined ? undefined : headerCarousel.header
    ),
    carouselId: carousel === undefined ? null : carousel.carouselId,
    resolvedCount:
      carousel === undefined || carousel.resolvedCount === undefined
        ? items.length
        : carousel.resolvedCount
  };
};

/**
 * Maps one further page of the flat list's carousel — fetched by
 * `loadMoreMyHubSavedList` via `GET /v1/myHub/carousels/{carouselId}` — onto
 * the rows it appends.
 *
 * Mirrors `mapMyHubCarouselToTilePage` (`myHubMappers.ts`), the same shape
 * carousel row tiles page with, but producing {@link TMyHubSavedItem} rows
 * instead of {@link TCarouselItem} tiles.
 *
 * @param carousel - One page of the carousel backing the flat list.
 * @param imageHostBaseUrl - Image host resolved once per fetch.
 * @param filter - The tab being paged — see {@link mapMyHubSavedItem}.
 * @returns The page's rows and the carousel's current `resolvedCount`.
 */
export const mapMyHubSavedPage = (
  carousel: TMyHubCarousel,
  imageHostBaseUrl: string = "",
  filter: MyHubFilterId = MyHubFilterId.SAVED
): { items: TMyHubSavedItem[]; resolvedCount: number } => {
  const items: TMyHubSavedItem[] = carousel.items
    .filter(
      (item: TMyHubItemUnion): item is TRecordingMyHubItem =>
        item.myHubItemType === TMyHubType.RECORDING
    )
    .map(
      (item: TRecordingMyHubItem): TMyHubSavedItem =>
        mapMyHubSavedItem(item, imageHostBaseUrl, filter)
    );

  return {
    items,
    resolvedCount:
      carousel.resolvedCount !== undefined
        ? carousel.resolvedCount
        : items.length
  };
};

// ── Tab-level copy ────────────────────────────────────────────────────────────
// Composed here rather than in the container for the same reason the row copy is
// (see this module's header): it is derived state, so the View should read the
// finished string instead of deriving it. That also makes the wording and the
// rounding testable without React.

/** Separator between the two halves of the Saved summary — Figma node 115:988. */
const SUMMARY_SEPARATOR: string = " ‧ ";

/**
 * The Deleted tab's separator — Figma node 71:2338 reads "Total Deleted: 3
 * recordings / used 10 hours", a slash rather than the Saved tab's middot.
 */
const DELETED_SUMMARY_SEPARATOR: string = " / ";

/**
 * Copy that differs between the two tabs sharing the flat list. Everything else —
 * the rows, the actions, the layout — is identical, so this table is the whole of
 * the difference and a third flat tab would only have to add a row to it.
 */
type TFlatListCopy = {
  countKey: string;
  emptyKey: string;
};

const FLAT_LIST_COPY: Readonly<Record<string, TFlatListCopy>> = {
  [MyHubFilterId.SAVED]: {
    countKey: LocaleKeys.MY_HUB_SAVED_SUMMARY_COUNT,
    emptyKey: LocaleKeys.MY_HUB_SAVED_EMPTY_MESSAGE
  },
  [MyHubFilterId.DELETED]: {
    countKey: LocaleKeys.MY_HUB_DELETED_SUMMARY_COUNT,
    emptyKey: LocaleKeys.MY_HUB_DELETED_EMPTY_MESSAGE
  }
};

/** Falls back to the Saved wording for any filter not in the table. */
const copyFor = (filter: MyHubFilterId): TFlatListCopy => {
  const copy: TFlatListCopy | undefined = FLAT_LIST_COPY[filter];
  // `||` rather than `??` or a ternary: the lookup yields an object or
  // `undefined`, so `undefined` is the only falsy case it can catch.
  return copy || FLAT_LIST_COPY[MyHubFilterId.SAVED];
};

/**
 * Composes the summary line above the flat list — "Total Saved: 3 recordings ‧
 * 10 / 10 hours" (Figma node 115:988) or "Total Deleted: 3 recordings / used 10
 * hours" (node 71:2338).
 *
 * Whole hours, because both designs show the figures in hours and the API
 * reports them in minutes. The second half is dropped when the API did not
 * supply the figures behind it, so the line never reads "0 / 0 hours" as though
 * that were a real allowance.
 *
 * The two tabs state different things with those minutes: Saved has an allowance
 * to spend ("10 / 10 hours"), Deleted only reports consumption ("used 10
 * hours"), which is why the Deleted half keys off `usedMinutes` while the Saved
 * half needs a `limitMinutes` to divide the used time against.
 *
 * @param summary - Mapped tab header, or `null` before the response arrives.
 * @param filter - The active tab.
 * @returns The composed line, or `""` when there is nothing to state.
 */
export const composeSavedSummaryText = (
  summary: TMyHubSavedListSummary | null,
  filter: MyHubFilterId
): string => {
  if (summary === null) {
    return "";
  }

  const isDeleted: boolean = filter === MyHubFilterId.DELETED;
  const parts: string[] = [];

  parts.push(
    interpolate(
      Locale.getString(copyFor(filter).countKey),
      String(summary.totalCount)
    )
  );

  const usedHours: number = Math.round(summary.usedMinutes / MINUTES_PER_HOUR);

  if (isDeleted) {
    if (summary.usedMinutes > 0) {
      parts.push(
        interpolate(
          Locale.getString(LocaleKeys.MY_HUB_DELETED_SUMMARY_USED),
          String(usedHours)
        )
      );
    }
    return parts.join(DELETED_SUMMARY_SEPARATOR);
  }

  if (summary.limitMinutes > 0) {
    const limitHours: number = Math.round(
      summary.limitMinutes / MINUTES_PER_HOUR
    );
    parts.push(
      interpolate(
        Locale.getString(LocaleKeys.MY_HUB_SAVED_SUMMARY_HOURS),
        String(usedHours),
        String(limitHours)
      )
    );
  }

  return parts.join(SUMMARY_SEPARATOR);
};

/**
 * Label for the tab-level bulk button, or `""` when the response declared no
 * bulk action and the button is not rendered.
 *
 * Driven by the header's `bulkAction` rather than by the tab, because the button
 * exists at the service's discretion — a Deleted tab whose response omits it
 * must not grow one.
 *
 * @param bulkAction - The declared bulk action, or `null`.
 * @returns The localized label, or `""`.
 */
export const composeSavedBulkActionLabel = (
  bulkAction: TMyHubBulkActionType | null
): string => {
  if (bulkAction === TMyHubBulkActionType.PERMANENTLY_DELETE_ALL) {
    return Locale.getString(LocaleKeys.MY_HUB_DELETED_PERMANENT_CONFIRM_ACCEPT);
  }
  // Every other bulk action belongs to a folder header rather than to this
  // list — render nothing rather than an unlabelled button.
  return "";
};

/**
 * The tab's empty-list message, worded per tab.
 *
 * @param filter - The active tab.
 * @returns The localized message.
 */
export const composeSavedEmptyMessage = (filter: MyHubFilterId): string => {
  return Locale.getString(copyFor(filter).emptyKey);
};
